// package imports
import { useRef } from 'react';
import { Session } from 'next-auth';
import { AriaMenuOptions } from 'react-aria';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

// local imports
import SlideInMenu from './SlideInMenu';
import { SiteHeaderType } from '../SiteHeaderTypes';
import HorizontalSeparator from 'components/common/HorizontalSeperator';
import MainMenuItem from './MainMenuItem';
import { useMobileSiteHeader } from './MobileMenuContext';
import { SharedHeaderType } from '../SharedHeaderTypes';
import { hasRealityCoachAccess, isCustomer } from 'services/subscriptionUtils';

const MainMenu = ({
  data,
  session,
  menuProps,
  sharedHeaderFields,
}: {
  data: SiteHeaderType;
  session: Session | null;
  menuProps: AriaMenuOptions<unknown>;
  sharedHeaderFields: SharedHeaderType;
}) => {
  const { state } = useMobileSiteHeader();
  const portalRef = useRef<HTMLDivElement>(null);

  const mappedSections = data?.fields?.megaMenuSections
    .map((section) => {
      if (section.fields.realityCoachPermissionRequired === 'true') {
        if (hasRealityCoachAccess(session)) {
          return section;
        }
        return null;
      } else {
        return section;
      }
      if (
        section.fields.subscriptionRequiredForDisplay === 'true' &&
        !isCustomer(session?.user)
      ) {
        return null;
      }
      if (
        section.fields?.loggedInUserDisplayOnly === 'true' &&
        !session?.user
      ) {
        return null;
      }
      if (section.fields?.freeAccountDisplayOnly === 'true') {
        if (isCustomer(session?.user)) {
          return null;
        }
      }

      if (
        section.fields?.notLoggedInUserOnlyDisplay === 'true' &&
        session?.user
      ) {
        return null;
      }
      return section;
    })
    .filter((section) => section !== null)
    .sort((a, b) => {
      const aOrder = Number(a?.fields?.mobileDisplayOrder ?? 0);
      const bOrder = Number(b?.fields?.mobileDisplayOrder ?? 0);
      return aOrder - bOrder;
    });

  return (
    <SlideInMenu
      {...menuProps}
      className="flex flex-col bg-white fixed z-[1299] inset-0"
      isOpen={state.isOpen}
      autoFocus={false}
    >
      <div className="flex flex-none border-b border-gray-300">
        <form action="/search" className="flex flex-auto">
          <label className="flex flex-auto items-center px-4">
            <SearchIcon className="flex-none text-2xl mr-2 text-black" />
            <input
              type="search"
              name="q"
              placeholder="Search"
              className="flex-auto outline-none placeholder:font-serif placeholder:font-semibold"
            />
          </label>
        </form>
        <div className="flex-none">
          <button
            onClick={() => state.close()}
            type="button"
            aria-label="Close menu"
            className="flex items-center justify-center w-14 h-14 bg-light-blue text-white"
          >
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>
      </div>
      <div
        ref={portalRef}
        className="relative flex flex-col flex-auto overflow-hidden"
      >
        <div className="flex-auto overflow-auto overscroll-contain">
          <div className="pt-2 pb-4 w-full mx-auto px-4 md:px-6 lg:px-10 max-w-[1392px]">
            {mappedSections && (
              <>
                <div className="grid gap-2">
                  {mappedSections.map((section) => {
                    if (!section) return null;
                    return (
                      <div key={section.contentID}>
                        {section?.fields.isAccountSection === 'true' && (
                          <HorizontalSeparator
                            key={`sep${section.contentID}`}
                            className="my-2"
                          />
                        )}
                        <MainMenuItem
                          key={section.contentID}
                          section={section}
                          portalRef={portalRef}
                          session={session}
                        />
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          <SharedHeaderNavMobile
            id="shared-header-nav-mobile"
            style={{ display: state.isOpen ? 'grid' : 'none' }}
            iconUrl={sharedHeaderFields.fields.mainIcon?.url}
          >
            <li>
              <a
                className="primary-nav-link"
                href={sharedHeaderFields.fields.mainLink?.href}
                target="_blank"
                rel="noreferrer"
              >
                {sharedHeaderFields.fields.mainLabel}
              </a>
            </li>
            {sharedHeaderFields.fields.sites.map((link, index) => {
              return (
                <li key={`desktop-${index}`}>
                  <a
                    className={
                      link.fields.label?.toLocaleLowerCase() ===
                        'liftwithboystown.org' ||
                      link.fields.label?.toLocaleLowerCase() ===
                        'lift with boys town'
                        ? 'current-link'
                        : 'primary-nav-link'
                    }
                    href={link.fields.link.href}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {link.fields.label}
                  </a>
                </li>
              );
            })}
          </SharedHeaderNavMobile>
        </div>
      </div>
    </SlideInMenu>
  );
};

export default MainMenu;

export const SharedHeaderNavMobile = styled('ul', {
  shouldForwardProp: (prop) => prop !== 'iconUrl',
})<{ iconUrl?: string }>(({ theme, iconUrl }) => ({
  margin: '1rem 1rem 1rem 1rem',
  listStyle: 'none',
  gridTemplateColumns: 'auto auto',
  gap: '1px',
  alignItems: 'center',
  paddingLeft: 0,
  paddingRight: 0,
  '& > li a.current-link': {
    backgroundColor: theme.palette.gold.main,
  },
  '& > li:first-of-type a:before': {
    content: '""',
    backgroundImage: `url(${iconUrl})`,
    width: '20px',
    height: '20px',
    backgroundSize: 'cover',
    marginRight: '5px',
    display: 'inline-block',
  },
  '& > li': {
    backgroundColor: '#f1f1f1',
    '& > a': {
      display: 'flex',
      textDecoration: 'none',
      fontSize: '11px',
      lineHeight: '20px',
      color: 'rgb(19 61 96)',
      textTransform: 'uppercase',
      textAlign: 'left',
      fontWeight: '600',
      padding: '12px 0.5rem',
      border: 'none',
    },
  },
}));
